import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Contact,
  durationStringToMinutes,
  FormFieldType,
  MeetingLocationType,
  NotificationType,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { ServiceEventType } from '@vendasta/meetings';
import { TimeSlot } from '../bookme/bookme';
import { first } from 'rxjs/operators';
import { CONTACT_PREFILL_PARAM_NAMES, ContactPrefill, parsePhoneNumberToPhoneFields, readPrefillParam } from '../util';
import { resolveBookmeLocale } from '../localize-bootstrap.service';
import { resolveBookingSource, RWG_TOKEN_METADATA_KEY } from '../booking-request-id';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { customEmailValidator } from '../util';
import { CartItem } from '../shared/booking-cart/booking-cart.component';
import { MeetingForm } from '@vendasta/meetings';
import { DynamicFormField } from '../shared/dynamic-form/dynamic-form.interfaces';
import { buildDynamicFormField } from '../shared/dynamic-form/dynamic-form.mapper';
import { PhoneInputComponent } from '../shared/phone-input/phone-input.component';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
} from '../analytics/constants';

@Component({
  selector: 'app-confirm-booking-page',
  standalone: false,
  templateUrl: './confirm-booking-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./confirm-booking-page.component.scss'],
})
export class ConfirmBookingPageComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly recaptchaV3Service = inject(ReCaptchaV3Service);
  private readonly analytics = inject(ProductAnalyticsService);
  private readonly snackBar = inject(MatSnackBar);

  cartItems = signal<CartItem[]>([]);
  submitting = false;
  submissionError = signal('');
  isSingleEventRoute = false;

  scheduledDate = signal('');
  scheduledTime = signal('');

  /** Form controls */
  form = new UntypedFormGroup({
    firstName: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/\S/),
    ]),
    lastName: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/\S/),
    ]),
    email: new UntypedFormControl(''),
    phoneNumber: new UntypedFormControl(''),
    location: new UntypedFormControl(''),
    comment: new UntypedFormControl(''),
  });

  get phoneNumberControl(): FormControl {
    return this.form.get('phoneNumber') as FormControl;
  }

  /** Guest emails */
  showGuestEmails = false;
  guestEmails: string[] = [];
  separatorKeyCodes = [ENTER, COMMA];

  /** Custom form fields from selected event types */
  customFormFields: DynamicFormField[] = [];

  /** Contact prefill applied once the phone input and event data are ready. */
  private pendingPrefillCountryCode: string | null = null;
  private pendingInviteeEmails: string[] | null = null;
  private customFieldPrefillApplied = false;

  get customFieldsInvalid(): boolean {
    return this.customFormFields.some((field) => field.formControl.invalid);
  }

  /** Field visibility and required state derived from selected events */
  isEmailRequired = false;
  isPhoneRequired = false;
  canAddAdditionalGuests = false;
  showSmsNotice = false;
  showLocationField = false;
  locationInstructions = '';

  @ViewChild('guestEmailInput') guestEmailInput: ElementRef<HTMLInputElement>;
  @ViewChild(PhoneInputComponent) phoneInput: PhoneInputComponent;

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    this.isSingleEventRoute =
      !!params['calendarId'] && !!params['meetingTypeId'];

    const initialDraft = this.storeService.getCustomerInfoDraft();
    if (initialDraft) {
      this.form.patchValue(initialDraft.formValue);
      this.guestEmails = [...initialDraft.guestEmails];
      this.showGuestEmails = initialDraft.showGuestEmails;
    }
    let pendingCustomAnswers: Record<string, unknown> | null =
      initialDraft?.customAnswers ?? null;

    const contactPrefill = this.storeService.getContactPrefill();
    if (contactPrefill) {
      this.applyContactPrefill(contactPrefill);
    }

    this.storeService.selectedBookingEvents$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((events) => {
        this.cartItems.set(
          events.map((event) => ({
            name: event.name,
            providerName: this.storeService.getProviderNameForEvent(event),
            duration: event.duration
              ? durationStringToMinutes({ duration: event.duration })
              : 0,
            initials: this.getInitials(event.name),
          }))
        );

        this.customFormFields = this.extractCustomFormFields(events);
        this.deriveFieldRequirements(events);
        if (pendingCustomAnswers) {
          for (const field of this.customFormFields) {
            const saved = pendingCustomAnswers[field.id];
            if (saved !== undefined) {
              field.formControl.setValue(saved);
            }
          }
          pendingCustomAnswers = null;
        }
        this.applyCustomFieldPrefill();
        this.applyPendingInviteeEmails();
      });

    // Read time slot from store (immediate) and subscribe for updates
    const initialSlot = this.storeService.getSelectedTimeSlot();
    if (initialSlot?.start) {
      this.updateScheduleDisplay(initialSlot);
    }
    this.storeService.timeSlot$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((timeSlot) => {
        if (timeSlot?.start) {
          this.updateScheduleDisplay(timeSlot);
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.pendingPrefillCountryCode && this.phoneInput) {
      this.phoneInput.setCountryCode(this.pendingPrefillCountryCode);
      this.pendingPrefillCountryCode = null;
    }
  }

  /**
   * Seeds the contact form from a query-param link. Only empty controls are
   * filled, so a saved draft (already patched in) is never overwritten. Country
   * code and invitee emails are deferred until the phone input and event
   * requirements are ready. Values stay editable and are validated on submit.
   */
  private applyContactPrefill(prefill: ContactPrefill): void {
    const controls: Array<keyof ContactPrefill> = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'location',
      'comment',
    ];
    for (const key of controls) {
      const value = prefill[key];
      const control = this.form.get(key);
      if (typeof value === 'string' && value && control && !control.value) {
        control.setValue(value);
      }
    }
    this.pendingPrefillCountryCode = prefill.countryCode?.toUpperCase() ?? null;
    this.pendingInviteeEmails = prefill.inviteeEmails ?? null;
  }

  /**
   * Prefills meeting-type custom fields from msm_<fieldId> query params (old-flow
   * parity), reading the entry query string captured by booking-layout (via
   * readCustomFieldParam) so it shares the contact prefill's source and decoding.
   * Only empty fields are filled; runs once fields are available.
   */
  private applyCustomFieldPrefill(): void {
    if (this.customFieldPrefillApplied || this.customFormFields.length === 0) {
      return;
    }
    for (const field of this.customFormFields) {
      const current = field.formControl.value;
      if (current == null || current === '') {
        const value = this.readCustomFieldParam(field.id);
        if (value) {
          field.formControl.setValue(value);
        }
      }
    }
    this.customFieldPrefillApplied = true;
  }

  private readCustomFieldParam(fieldId: string): string {
    // Never let a custom field whose id collides with a contact key pull in
    // contact PII.
    if (CONTACT_PREFILL_PARAM_NAMES.includes(`msm_${fieldId}`)) {
      return '';
    }
    // Read from the entry query string captured by booking-layout (same source
    // and decoding as the contact prefill), so a redirect that cleared the
    // address bar doesn't turn this into a silent no-op.
    const search = this.storeService.getPrefillSearch();
    return search ? readPrefillParam(search, `msm_${fieldId}`) : '';
  }

  private applyPendingInviteeEmails(): void {
    if (!this.pendingInviteeEmails) {
      return;
    }
    if (this.guestEmails.length > 0) {
      this.pendingInviteeEmails = null;
      return;
    }
    if (!this.canAddAdditionalGuests) {
      return;
    }
    const valid = this.pendingInviteeEmails.filter((email) =>
      this.isValidEmail(email)
    );
    if (valid.length > 0) {
      this.guestEmails = valid;
      this.showGuestEmails = true;
    }
    this.pendingInviteeEmails = null;
  }

  onRemoveCartItem(index: number): void {
    const events = this.storeService.getSelectedBookingEvents();
    if (!events[index]) {
      return;
    }
    this.storeService.setSelectedBookingEvents(
      events.filter((_, i) => i !== index)
    );
  }

  toggleGuestEmails(): void {
    this.showGuestEmails = !this.showGuestEmails;
  }

  addGuestEmail(value: string): void {
    const email = value?.trim();
    if (
      email &&
      this.isValidEmail(email) &&
      !this.guestEmails.includes(email)
    ) {
      this.guestEmails = [...this.guestEmails, email];
    }
    if (this.guestEmailInput?.nativeElement) {
      this.guestEmailInput.nativeElement.value = '';
    }
  }

  removeGuestEmail(email: string): void {
    this.guestEmails = this.guestEmails.filter((e) => e !== email);
  }

  clearAllGuestEmails(): void {
    this.guestEmails = [];
    if (this.guestEmailInput?.nativeElement) {
      this.guestEmailInput.nativeElement.value = '';
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  }

  onScheduleAppointment(): void {
    if (this.submitting) {
      return;
    }
    if (this.form.invalid || this.customFieldsInvalid) {
      this.form.markAllAsTouched();
      this.customFormFields.forEach((field) => field.formControl.markAsTouched());
      return;
    }

    this.submitting = true;
    this.submissionError.set('');
    const events = this.storeService.getSelectedBookingEvents();
    const timeSlot = this.storeService.getSelectedTimeSlot();

    if (!timeSlot?.start || events.length === 0) {
      this.submitting = false;
      return;
    }

    this.recaptchaV3Service
      .execute('bookMeeting')
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (recaptchaToken) => {
          this.submitBooking(events, timeSlot.start, recaptchaToken);
        },
        error: () => {
          this.submitting = false;
        },
      });
  }

  /**
   * Harvests msm_/utm_ tracking params from the URL so they travel with the
   * booking as metadata. Downstream lead-routing keys off these to assign the
   * lead to the rep
   */
  private collectTrackingMetadata(): { [key: string]: string } {
    if (typeof window === 'undefined' || !window.location) {
      return {};
    }
    const params = new URLSearchParams(window.location.search);
    const metadata: { [key: string]: string } = {};
    params.forEach((value, key) => {
      // Contact PII params are prefill inputs, not lead-routing metadata.
      if (CONTACT_PREFILL_PARAM_NAMES.includes(key)) {
        return;
      }
      if (key.startsWith('msm_') || key.startsWith('utm_')) {
        metadata[key] = value;
      }
    });
    // Reserve with Google: forward the rwg_token from the redirect URL; the
    // backend owns conversion reporting and persistence.
    const rwgToken = params.get(RWG_TOKEN_METADATA_KEY);
    if (rwgToken) {
      metadata[RWG_TOKEN_METADATA_KEY] = rwgToken;
    }
    return metadata;
  }

  private submitBooking(
    events: ServiceEventType[],
    start: Date,
    recaptchaToken: string
  ): void {
    const formValue = this.form.value;
    const selectedHost = this.storeService.getSelectedHost();
    const eventHostMap = this.storeService.getEventHostMap();

    const targets = events.map((event, index) => {
      // Only CLIENT_CHOICE requires a specific host userId; other types (ROUND_ROBIN, PRIORITY_ROUND_ROBIN, MULTI_HOST) are handled server-side
      const isClientChoice = event.eventType === TeamEventMeetingType.CLIENT_CHOICE;
      const hostId = eventHostMap.get(event.id) || selectedHost || undefined;
      return {
        eventTypeId: event.id,
        userId: isClientChoice ? hostId : undefined,
        order: index,
      };
    });

    const phoneFields = parsePhoneNumberToPhoneFields(
      this.formatPhoneNumber(formValue.phoneNumber)
    );

    const attendees = [
      new Contact({
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: formValue.email,
        phoneFields,
        timeZone: { id: this.storeService.getTimezone() },
        isPrimary: true,
      }),
      ...this.guestEmails.map(
        (email) => new Contact({ email, isPrimary: false })
      ),
    ];

    // Build custom form answers, preserving each field's original meeting-type
    // type (so DROPDOWN/MULTIPLE_CHOICE aren't collapsed to SELECT).
    const formAnswers: MeetingForm | undefined =
      this.customFormFields.length > 0
        ? {
            formAnswers: this.customFormFields.map((field) => ({
              id: field.id,
              label: field.label,
              type: field.originalType ?? FormFieldType.FORM_FIELD_TYPE_TEXT,
              answer: Array.isArray(field.formControl.value)
                ? field.formControl.value
                : [field.formControl.value],
            })),
          }
        : undefined;

    const bookingOriginId =
      this.storeService.getBookingOriginId() || events[0]?.id || '';

    const isMultiService = events.length > 1;
    const eventTypeIds = events.map((e) => e.id);
    const totalDurationMinutes = events.reduce(
      (sum, e) =>
        sum +
        (e.duration ? durationStringToMinutes({ duration: e.duration }) : 0),
      0
    );

    this.storeService
      .bookBatchMeeting({
        targets,
        start,
        attendees,
        comment: formValue.comment,
        location: formValue.location,
        formAnswers,
        metadata: this.collectTrackingMetadata(),
        recaptchaToken,
        bookingSource: resolveBookingSource(),
        bookingOriginId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bookingGroupId) => {
          this.submitting = false;
          this.storeService.clearCustomerInfoDraft();
          this.storeService.clearContactPrefill();
          this.storeService.clearPrefillSearch();
          if (isMultiService) {
            this.analytics.trackEvent(
              POSTHOG_KEYS.MULTI_SERVICE_BOOKING_CONFIRMED,
              POSTHOG_CATEGORIES.MULTI_SERVICE_BOOKING,
              POSTHOG_ACTIONS.CHECKOUT,
              events.length,
              {
                cartSize: events.length,
                eventTypeIds,
                totalDurationMinutes,
              }
            );
          }
          const originId = this.storeService.getBookingOriginId();
          if (originId.startsWith('GRP-') || originId.startsWith('SVS-')) {
            const groupServiceRedirect =
              this.storeService.getCurrentState().bookingItemRedirect;
            if (groupServiceRedirect?.enabled && groupServiceRedirect.url) {
              this.storeService.setPendingRedirect(
                groupServiceRedirect.url,
                groupServiceRedirect.delaySeconds
              );
            }
          } else {
            const bookedMeetingType =
              this.storeService.getCurrentState().meetingType;
            if (
              bookedMeetingType?.redirectEnabled &&
              bookedMeetingType?.redirectUrl
            ) {
              this.storeService.setPendingRedirect(
                bookedMeetingType.redirectUrl,
                bookedMeetingType.redirectDelaySeconds
              );
            }
          }
          this.router.navigate(['/meeting-info', bookingGroupId], {
            queryParamsHandling: 'merge',
          });
        },
        error: (err) => {
          this.submitting = false;
          if (err.status === 409) {
            this.storeService.setErrorDisplay('conflict-booking');
            return;
          }
          const message = (
            err?.error?.message ||
            err?.message ||
            ''
          ).toLowerCase();
          // Daily meeting limit reached between slot-selection and submit: send the guest back to the
          // time picker to choose another slot. Matches the backend's stable token, not human wording.
          if (message.includes('daily_meeting_limit_reached')) {
            const hasEvents =
              this.storeService.getSelectedBookingEvents().length > 0;
            this.storeService.setBookingStep(
              hasEvents ? 'datetime' : 'scheduling'
            );
            this.snackBar.open(
              $localize`:@@BOOK.TIME_SLOT_UNAVAILABLE_DESCRIPTION:The time slot you selected is no longer available. Please choose a new time to continue.`,
              '',
              { duration: 6000 }
            );
            return;
          }
          const isTerminal =
            message.includes('no longer exists') ||
            message.includes('expired') ||
            message.includes('host calendar/meeting app link is not connected');
          if (isTerminal) {
            this.storeService.handleMeetingTypeError(err);
          } else {
            this.submissionError.set(
              $localize`:@@BOOK.SUBMISSION_ERROR:Something went wrong while submitting your booking. Please try again.`
            );
          }
        },
      });
  }

  onBack(): void {
    this.saveFormDraft();
    this.storeService.setBookingStep('datetime');
  }

  onMakeChanges(): void {
    this.saveFormDraft();
    if (this.isSingleEventRoute) {
      const providerNeeded = this.storeService.getShouldShowProviderStep() && !this.storeService.getProviderStepSkipped();
      const step = providerNeeded ? 'provider' : 'datetime';
      this.storeService.setBookingStep(step);
    } else {
      this.storeService.setBookingStep('scheduling');
    }
  }

  private saveFormDraft(): void {
    const customAnswers: Record<string, unknown> = {};
    for (const field of this.customFormFields) {
      customAnswers[field.id] = field.formControl.value;
    }
    this.storeService.setCustomerInfoDraft({
      formValue: this.form.value,
      guestEmails: [...this.guestEmails],
      showGuestEmails: this.showGuestEmails,
      customAnswers,
    });
  }

  private deriveFieldRequirements(events: ServiceEventType[]): void {
    const storeState = this.storeService.getCurrentState();
    const meetingTypePhoneRequired =
      storeState?.meetingType?.isPhoneNumberRequired === true;
    const meetingTypeEmailRequired =
      storeState?.meetingType?.isEmailRequired === true;

    // A service sends an email notification when its channel is not SMS (EMAIL, or unset).
    const anyEventSendsEmail = events.some(
      (e) =>
        e.notificationType === undefined ||
        e.notificationType === null ||
        e.notificationType === NotificationType.EMAIL ||
        e.notificationType === NotificationType.BOTH
    );

    // Additional guests are email-only, so they can be invited whenever at least one selected
    // service emails — i.e. all-email or mixed bundles, but never an all-SMS bundle.
    this.canAddAdditionalGuests = anyEventSendsEmail;

    // A contact field is mandatory when an event explicitly requires it or
    // notifies the guest through that channel.
    this.isEmailRequired =
      meetingTypeEmailRequired ||
      events.some(
        (e) =>
          e.emailRequired === true

      );

    this.isPhoneRequired =
      meetingTypePhoneRequired ||
      events.some(
        (e) =>
          e.smsRequired === true
      );

    // SMS consent disclosure is only shown when notification channel is SMS
    // (or BOTH). Email-only bookings never show the banner, even when the
    // phone field is also required.
    this.showSmsNotice = events.some(
      (e) =>
        e.notificationType === NotificationType.SMS ||
        e.notificationType === NotificationType.BOTH
    );

    this.showLocationField = events.some(
      (e) => e.locationType === MeetingLocationType.IN_PERSON_CLIENT_SITE
    );

    const clientSiteEvent = events.find(
      (e) =>
        e.locationType === MeetingLocationType.IN_PERSON_CLIENT_SITE &&
        !!e.locationGuideline?.trim()
    );
    this.locationInstructions =
      clientSiteEvent?.locationGuideline?.trim() ?? '';

    // Update email validators
    const emailControl = this.form.get('email');
    if (this.isEmailRequired) {
      emailControl.setValidators([Validators.required, customEmailValidator()]);
    } else {
      emailControl.setValidators([customEmailValidator()]);
    }
    emailControl.updateValueAndValidity();

    // Update phone validators
    const phoneControl = this.form.get('phoneNumber');
    phoneControl.setValidators(
      this.isPhoneRequired ? [Validators.required] : []
    );
    phoneControl.updateValueAndValidity();

    // Update location validators
    const locationControl = this.form.get('location');
    if (this.showLocationField) {
      locationControl.setValidators([
        Validators.required,
        Validators.pattern(/\S/),
      ]);
    } else {
      locationControl.clearValidators();
    }
    locationControl.updateValueAndValidity();
  }

  private formatPhoneNumber(
    rawPhone: string | null | undefined
  ): string | undefined {
    if (!rawPhone || !this.phoneInput) {
      return undefined;
    }
    const countryCallingCode =
      this.phoneInput.countryDetails.countryCallingCode.toString();
    const countryCode = this.phoneInput.countryDetails.countryCode;
    let phoneNumber = rawPhone.toString().replace(/\D/g, '');
    if (phoneNumber.startsWith(countryCallingCode)) {
      phoneNumber = phoneNumber.substring(countryCallingCode.length);
    }
    return `${countryCallingCode}-${phoneNumber}-${countryCode}`;
  }

  private updateScheduleDisplay(timeSlot: TimeSlot): void {
    const tz =
      this.storeService.getTimezone() ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = resolveBookmeLocale();
    const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZone: tz };
    this.scheduledDate.set(
      timeSlot.start.toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric', timeZone: tz })
    );
    this.scheduledTime.set(
      timeSlot.end
        ? new Intl.DateTimeFormat(locale, timeOptions).formatRange(timeSlot.start, timeSlot.end)
        : timeSlot.start.toLocaleTimeString(locale, timeOptions)
    );
  }

  private getInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  private extractCustomFormFields(events: ServiceEventType[]): DynamicFormField[] {
    const fields: DynamicFormField[] = [];
    const seenFieldIds = new Set<string>();

    for (const event of events) {
      if (!event.serviceForm?.fields?.length) {
        continue;
      }

      for (const field of event.serviceForm.fields) {
        if (!field.id || seenFieldIds.has(field.id) || field.type === FormFieldType.FORM_FIELD_TYPE_INVALID) {
          continue;
        }

        seenFieldIds.add(field.id);
        const base = field.required ? [Validators.required, Validators.pattern(/\S/)] : [];

        const selectOptions =
          field.type === FormFieldType.FORM_FIELD_TYPE_SELECT
            ? 'config' in field && (field as Record<string, unknown>).config
              ? ((field as Record<string, unknown>).config as { options?: { text: string; value: string }[] })
                  ?.options || []
              : []
            : [];

        const built = buildDynamicFormField(
          { id: field.id, label: field.label || '', required: field.required || false, type: field.type, options: field.options },
          base,
          { selectOptions, defaultValue: '', defaultArrayValue: [] }
        );
        if (built) {
          fields.push(built);
        }
      }
    }

    return fields;
  }
}
